import { useState } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Shield, Search, Loader2, X, Clock, Check } from "lucide-react";

export default function ConsentVault() {
    const [email, setEmail] = useState("");
    const [consents, setConsents] = useState(null);
    const [loading, setLoading] = useState(false);
    const [revoking, setRevoking] = useState(null);

    const lookup = async () => {
        if (!email.trim()) return;
        setLoading(true);
        try {
            const res = await API.get(`/vault/lookup?email=${encodeURIComponent(email.trim())}`);
            setConsents(res.data.consents || []);
        } catch { toast.error("Lookup failed"); }
        setLoading(false);
    };

    const revoke = async (consentId) => {
        setRevoking(consentId);
        try {
            await API.post(`/vault/revoke/${consentId}`, { email: email.trim() });
            toast.success("Consent revoked");
            lookup();
        } catch { toast.error("Failed to revoke"); }
        setRevoking(null);
    };

    return (
        <div className="min-h-screen-safe bg-[#050505] px-6 py-16">
            <div className="max-w-2xl mx-auto space-y-8">
                <div className="text-center space-y-4">
                    <Shield className="h-10 w-10 text-[#047857] mx-auto" />
                    <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="vault-title">Consent Vault</h1>
                    <p className="text-[#9CA3AF]">Look up and manage your consent records. See who has your data and revoke access anytime.</p>
                </div>

                <div className="flex gap-2">
                    <input
                        type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && lookup()}
                        placeholder="Enter your email address"
                        className="flex-1 h-12 px-4 rounded-lg bg-[#0a0a0a] border border-white/10 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#047857]"
                        data-testid="vault-email-input"
                    />
                    <Button onClick={lookup} disabled={loading} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)] h-12 px-6" data-testid="vault-lookup-btn">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                </div>

                {consents !== null && (
                    consents.length > 0 ? (
                        <div className="space-y-3" data-testid="consent-list">
                            {consents.map((c) => {
                                const expired = new Date(c.opt_in_expires) < new Date();
                                return (
                                    <Card key={c.id} className="bg-[#0a0a0a] border-white/5">
                                        <CardContent className="p-4 flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>{c.business_name}</p>
                                                <div className="flex items-center gap-3 mt-1 text-xs text-[#6B7280]">
                                                    <Badge variant="outline" className="text-[10px]">Tier {c.tier}</Badge>
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {expired ? "Expired" : `Expires ${new Date(c.opt_in_expires).toLocaleDateString()}`}
                                                    </span>
                                                    <span>{new Date(c.created_at).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                            {!expired && (
                                                <Button size="sm" variant="outline" onClick={() => revoke(c.id)} disabled={revoking === c.id}
                                                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8" data-testid={`revoke-${c.id}`}>
                                                    {revoking === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3 mr-1" />}Revoke
                                                </Button>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-center text-sm text-[#6B7280]">No consent records found for this email.</p>
                    )
                )}

                <p className="text-center text-xs text-[#6B7280]/60">
                    <Link to="/" className="text-[#047857] hover:underline">The Irish Goodbye</Link> — Your data, your terms.
                </p>
            </div>
        </div>
    );
}
